// frontend/src/api/admin.ts
import { ApiError } from './auth';
import { API_BASE_URL } from '@/config/apiConfig';
import { createApiClient, isAxiosError } from './client';

// --- CORRECTED IMPORTS FROM SCHEMAS.TS ---
import { 
    ApiMatchSchedule,
    ApiDfsContest,
    ApiScoreRecalculationSeasonPayload,
    ApiAdminTaskResponse,
    ContestIdentifierPayload,
    PlayerRead
} from './schemas'; // This path assumes schemas.ts is in the same src/api/ directory
// --- END CORRECTED IMPORTS ---

const adminApiClient = createApiClient({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- API Function to Fetch Upcoming Schedule (Admin) ---
export const fetchUpcomingScheduleAdmin = async (token: string, days: number): Promise<ApiMatchSchedule[]> => {
    if (!token) throw new ApiError("Authentication token is required.", 401);
    const url = `${API_BASE_URL}/admin/schedule/upcoming?days=${days}`;
    try {
        const response = await adminApiClient.get<ApiMatchSchedule[]>(url, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to fetch upcoming schedule.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            if (status === 401 || status === 403) {
                errorMessage = 'Authentication/Authorization failed.';
            } else {
                const backendError = error.response?.data?.detail;
                if (typeof backendError === 'string') errorMessage = backendError;
            }
        }
        console.error(`[Admin API] Error fetching schedule:`, errorMessage, error);
        throw new ApiError(errorMessage, status);
    }
};

// --- API Function to Update Kickoff Time (Admin) ---
export const updateKickoffTimeAdmin = async (
    token: string,
    scheduleId: number,
    kickoffTimeUtc: string
): Promise<ApiMatchSchedule> => {
    if (!token) throw new ApiError("Authentication token is required.", 401);
    const url = `${API_BASE_URL}/admin/schedule/${scheduleId}/kickoff`;
    try {
        const payload = { kickoff_time_utc: kickoffTimeUtc };
        const response = await adminApiClient.patch<ApiMatchSchedule>(url, payload, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to update kickoff time for schedule ID ${scheduleId}.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            const backendError = error.response?.data?.detail;
            if (typeof backendError === 'string') { errorMessage = backendError; }
            else if (status === 400 ) { errorMessage = backendError || "Invalid data for kickoff."}
            else if (status === 404 ) { errorMessage = `Schedule entry ${scheduleId} not found.`}
        }
        console.error(`[Admin API] Error updating kickoff for ${scheduleId}:`, errorMessage, error);
        throw new ApiError(errorMessage, status);
    }
};

// --- API Function to Fetch Admin Contests ---
export const fetchAdminContests = async (
    token: string,
    filters: { is_finalized?: boolean; date_from?: string; date_to?: string } = {}
): Promise<ApiDfsContest[]> => {
    if (!token) throw new ApiError("Authentication token is required.", 401);
    const urlUpcoming = `${API_BASE_URL}/dfs/contests/upcoming?days=90`;
    const urlPast = `${API_BASE_URL}/dfs/contests/past?days_ago=90`;
    const urlActive = `${API_BASE_URL}/dfs/contests/active`;
    try {
        const [upcomingRes, pastRes, activeRes] = await Promise.all([
            adminApiClient.get<ApiDfsContest[]>(urlUpcoming, { headers: { Authorization: `Bearer ${token}` } }),
            adminApiClient.get<ApiDfsContest[]>(urlPast, { headers: { Authorization: `Bearer ${token}` } }),
            adminApiClient.get<ApiDfsContest[]>(urlActive, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        const allContests: ApiDfsContest[] = []; const contestIds = new Set<number>();
        const addContests = (contests: ApiDfsContest[]) => {
            contests.forEach(contest => { if (contest && typeof contest.id === 'number' && !contestIds.has(contest.id)) { allContests.push(contest); contestIds.add(contest.id); }});
        };
        addContests(activeRes.data); addContests(upcomingRes.data); addContests(pastRes.data);
        let filteredContests = allContests;
        if (typeof filters.is_finalized === 'boolean') {
            filteredContests = allContests.filter(c => c.is_finalized === filters.is_finalized);
        }
        filteredContests.sort((a, b) => new Date(b.start_time || b.contest_date || 0).getTime() - new Date(a.start_time || a.contest_date || 0).getTime());
        return filteredContests;
    } catch (error: any) {
        let errorMessage = `Failed to fetch contests for admin.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            if (status === 401 || status === 403) errorMessage = 'Authentication/Authorization failed.';
            else { const backendError = error.response?.data?.detail; if (typeof backendError === 'string') errorMessage = backendError; }
        }
        console.error(`[Admin API] Error fetching admin contests:`, errorMessage, error);
        throw new ApiError(errorMessage, status);
    }
};

// --- API Function to Finalize Contest ---
export const finalizeContestAdmin = async (
    token: string,
    contestId: number
): Promise<ApiDfsContest> => {
    if (!token) throw new ApiError("Authentication token is required.", 401);
    const url = `${API_BASE_URL}/admin/contests/${contestId}/finalize`;
    try {
        const response = await adminApiClient.patch<ApiDfsContest>(url, {}, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to finalize contest ${contestId}.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            const backendError = error.response?.data?.detail;
            if (typeof backendError === 'string') { errorMessage = backendError; }
            else if (status === 404) { errorMessage = `Contest ${contestId} not found.`; }
            else if (status === 409) { errorMessage = `Conflict finalizing contest ${contestId}.`; }
        }
        console.error(`[Admin API] Error finalizing contest ${contestId}:`, errorMessage, error);
        throw new ApiError(errorMessage, status);
    }
};

// --- API FUNCTIONS FOR AGGREGATION TASKS ---
export interface ApiAdminSeasonOption {
  competition_id: number;
  competition_name: string;
  season_identifier: string;
  is_active_season: boolean;
}

/** Seasons (active first) for the aggregation panel's single dropdown. */
export const fetchAdminSeasons = async (token: string): Promise<ApiAdminSeasonOption[]> => {
  if (!token) throw new ApiError("Authentication token is required.", 401);
  const url = `${API_BASE_URL}/admin/seasons`;
  try {
    const response = await adminApiClient.get<ApiAdminSeasonOption[]>(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new ApiError("Failed to load seasons for admin picker.", error?.response?.status);
  }
};

export const triggerPlayerSeasonAggregationAdmin = async (
  token: string,
  payload: ApiScoreRecalculationSeasonPayload
): Promise<ApiAdminTaskResponse> => {
  if (!token) throw new ApiError("Authentication token is required.", 401);
  const url = `${API_BASE_URL}/admin/stats/trigger-player-season-aggregation`;
  try {
    const response = await adminApiClient.post<ApiAdminTaskResponse>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to trigger player season stats aggregation.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
        status = error.response?.status;
        errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error.message) { 
        errorMessage = error.message;
    }
    console.error(`[Admin API] Error triggering player season aggregation:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};

export interface ApiDraftRankingPreviewRow {
  player_id: number; name: string; position: string | null; team: string | null;
  score: number; last_total: number | null; last_fppg: number | null;
  prev_total: number | null; prev_fppg: number | null;
}

export interface ApiDraftRankingsSummary {
  competition_id: number;
  written: number;
  existing_rows: number;
  pool_size: number;
  scored: number;
  coverage: number;
  dry_run: boolean;
  /** Present only when the run was refused (coverage floor / no data). */
  refused?: string;
  preview: ApiDraftRankingPreviewRow[];
}

/** Pre-draft rankings (two-season percentile blend). dryRun computes and
    returns the preview WITHOUT writing; otherwise replaces player_rankings. */
export const generateDraftRankingsAdmin = async (
  token: string,
  competitionId: number,
  dryRun: boolean = false
): Promise<ApiDraftRankingsSummary> => {
  if (!token) throw new ApiError("Authentication token is required.", 401);
  const url = `${API_BASE_URL}/admin/draft-rankings/generate`;
  try {
    const response = await adminApiClient.post<ApiDraftRankingsSummary>(
      url,
      { competition_id: competitionId, dry_run: dryRun },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  } catch (error: unknown) {
    let errorMessage = `Failed to generate draft rankings.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
        status = error.response?.status;
        errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error instanceof Error && error.message) {
        errorMessage = error.message;
    }
    console.error(`[Admin API] Error generating draft rankings:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};

export const triggerTeamDefenseAggregationAdmin = async (
  token: string,
  payload: ApiScoreRecalculationSeasonPayload
): Promise<ApiAdminTaskResponse> => {
  if (!token) throw new ApiError("Authentication token is required.", 401);
  const url = `${API_BASE_URL}/admin/stats/trigger-team-defense-aggregation`;
  try {
    const response = await adminApiClient.post<ApiAdminTaskResponse>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to trigger team defensive stats aggregation.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
        status = error.response?.status;
        errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error.message) {
        errorMessage = error.message;
    }
    console.error(`[Admin API] Error triggering team defense aggregation:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};

export const triggerTeamOffenseAggregationAdmin = async (
  token: string,
  payload: ApiScoreRecalculationSeasonPayload
): Promise<ApiAdminTaskResponse> => {
  if (!token) throw new ApiError("Authentication token is required.", 401);
  const url = `${API_BASE_URL}/admin/stats/trigger-team-offense-aggregation`;
  try {
    const response = await adminApiClient.post<ApiAdminTaskResponse>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to trigger team offensive stats aggregation.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
        status = error.response?.status;
        errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error.message) {
        errorMessage = error.message;
    }
    console.error(`[Admin API] Error triggering team offense aggregation:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};

export const triggerLeagueAverageDefenseAggregationAdmin = async (
  token: string,
  payload: ApiScoreRecalculationSeasonPayload
): Promise<ApiAdminTaskResponse> => {
  if (!token) throw new ApiError("Authentication token is required.", 401);
  const url = `${API_BASE_URL}/admin/stats/trigger-league-average-defense-aggregation`;
  try {
    const response = await adminApiClient.post<ApiAdminTaskResponse>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to trigger league average defensive stats aggregation.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
        status = error.response?.status;
        errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error.message) {
        errorMessage = error.message;
    }
    console.error(`[Admin API] Error triggering league average defense aggregation:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};

// --- NEW API FUNCTIONS FOR KICKOFF WORKFLOW ---
export const triggerSyncPlayerLockTimesAdmin = async (
  token: string,
  startDate?: string | null,
  endDate?: string | null
): Promise<ApiAdminTaskResponse> => {
  if (!token) throw new ApiError("Authentication token is required.", 401);
  const url = `${API_BASE_URL}/admin/actions/sync-player-lock-times`;
  const params: Record<string, string> = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;

  console.log(`[Admin API] Triggering Sync Player Lock Times. Params:`, params);
  try {
    const response = await adminApiClient.post<ApiAdminTaskResponse>(url, {}, { // Empty body
      headers: { Authorization: `Bearer ${token}` },
      params: params 
    });
    console.log(`[Admin API] Sync Player Lock Times task enqueued: ${response.data.message}`);
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to trigger sync player lock times.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
        status = error.response?.status;
        errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error.message) {
        errorMessage = error.message;
    }
    console.error(`[Admin API] Error triggering sync player lock times:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};

export const triggerResyncContestCompositionsAdmin = async (
  token: string,
  daysAhead?: number | null,
  forceStartTimeUpdate: boolean = false
): Promise<ApiAdminTaskResponse> => {
  if (!token) throw new ApiError("Authentication token is required.", 401);
  const url = `${API_BASE_URL}/admin/actions/resync-contest-compositions`;
  const params: Record<string, string> = {};
  if (daysAhead !== null && daysAhead !== undefined) params.days_ahead_param = String(daysAhead);
  if (forceStartTimeUpdate) params.force_start_time_update = 'true';

  console.log(`[Admin API] Triggering Resync Contest Compositions. Params:`, params);
  try {
    const response = await adminApiClient.post<ApiAdminTaskResponse>(url, {}, { // Empty body
      headers: { Authorization: `Bearer ${token}` },
      params: params
    });
    console.log(`[Admin API] Resync Contest Compositions task enqueued: ${response.data.message}`);
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to trigger resync contest compositions.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
        status = error.response?.status;
        errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error.message) {
        errorMessage = error.message;
    }
    console.error(`[Admin API] Error triggering resync contest compositions:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};


export const triggerSalaryCalculationAdmin = async (
  token: string,
  payload: ContestIdentifierPayload
): Promise<ApiAdminTaskResponse> => {
  if (!token) throw new ApiError("Authentication token is required.", 401);
  const url = `${API_BASE_URL}/admin/salaries/trigger-calculation`;
  try {
    const response = await adminApiClient.post<ApiAdminTaskResponse>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to trigger salary calculation.`;
    let status: number | undefined;
    if (isAxiosError(error)) { // Ensure axios is imported at the top
        status = error.response?.status;
        errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error.message) { 
        errorMessage = error.message;
    }
    console.error(`[Admin API] Error triggering salary calculation:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};

export const fetchPlayersWithMissingAttributes = async (
  token: string
): Promise<PlayerRead[]> => { // No longer needs context and contextId
  if (!token) throw new ApiError("Authentication is required.", 401);
  
  const url = `${API_BASE_URL}/admin/players/unassigned-position`; // URL is now simpler
  
  try {
    const response = await adminApiClient.get<PlayerRead[]>(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch players with missing attributes.`;
    if (isAxiosError(error)) {
      errorMessage = error.response?.data?.detail || errorMessage;
    }
    throw new ApiError(errorMessage, error.response?.status);
  }
};

export const updatePlayerAttributes = async (
  token: string,
  playerId: number,
  // The payload now matches our new schema: an object with a single key.
  data: { primary_position: 'FW' | 'MF' | 'DF' | 'GK' | null }
): Promise<PlayerRead> => {
  if (!token) throw new ApiError("Authentication is required.", 401);
  
  const url = `${API_BASE_URL}/admin/players/${playerId}/attributes`;
  
  try {
    const response = await adminApiClient.patch<PlayerRead>(url, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to update attributes for player ${playerId}.`;
    if (isAxiosError(error)) {
      errorMessage = error.response?.data?.detail || errorMessage;
    }
    throw new ApiError(errorMessage, error.response?.status);
  }
};
// --- END NEW API FUNCTIONS ---

// === ADD THIS ENTIRE NEW FUNCTION ===
export const fetchPlayersNeedingOffboarding = async (
  token: string
): Promise<PlayerRead[]> => {
  if (!token) throw new ApiError("Authentication is required.", 401);
  
  const url = `${API_BASE_URL}/admin/players/needs-offboarding`;

  try {
    const response = await adminApiClient.get<PlayerRead[]>(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch players needing offboarding review.`;
    if (isAxiosError(error)) {
      errorMessage = error.response?.data?.detail || errorMessage;
    }
    throw new ApiError(errorMessage, error.response?.status);
  }
};
// === END OF NEW FUNCTION ===


// === Account deletion (stage 2) — admin-side ===
export interface PendingDeletionUser {
  id: number;
  username: string;
  email: string | null;
  phone_number: string | null;
}

export const fetchUsersPendingDeletion = async (token: string): Promise<PendingDeletionUser[]> => {
  if (!token) throw new ApiError("Authentication is required.", 401);
  const url = `${API_BASE_URL}/admin/users/pending-deletion`;
  try {
    const response = await adminApiClient.get<PendingDeletionUser[]>(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch users pending deletion.`;
    if (isAxiosError(error)) {
      errorMessage = error.response?.data?.detail || errorMessage;
    }
    throw new ApiError(errorMessage, error.response?.status);
  }
};

export const purgeUserPersonalData = async (token: string, userId: number): Promise<{ message: string }> => {
  if (!token) throw new ApiError("Authentication is required.", 401);
  const url = `${API_BASE_URL}/admin/users/${userId}/purge`;
  try {
    const response = await adminApiClient.post<{ message: string }>(url, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to purge user ${userId}.`;
    if (isAxiosError(error)) {
      errorMessage = error.response?.data?.detail || errorMessage;
    }
    throw new ApiError(errorMessage, error.response?.status);
  }
};
// --- Split season (SPLIT-SEASON.md §4): season dates + fantasy-season boundaries ---
export interface SeasonDatesPayload {
  season_start_calendar_date: string;   // YYYY-MM-DD
  season_end_calendar_date: string;
  fall_season_end_date?: string | null;      // Fall championship week's date
  spring_season_start_date?: string | null;  // Spring first-round date
  spring_season_end_date?: string | null;    // Spring championship round (GW37 rule - never the final day)
}
export interface SeasonDatesResult {
  status: string;
  warnings?: string[];
  [key: string]: any;
}
export const setSeasonDatesAdmin = async (
  token: string, fsdId: number, payload: SeasonDatesPayload
): Promise<SeasonDatesResult> => {
  if (!token) throw new ApiError("Authentication is required.", 401);
  const url = `${API_BASE_URL}/admin/fantasy-season/${fsdId}/dates`;
  try {
    const response = await adminApiClient.patch<SeasonDatesResult>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to set season dates.';
    if (isAxiosError(error)) {
      errorMessage = typeof error.response?.data?.detail === 'string' ? error.response.data.detail : errorMessage;
    }
    throw new ApiError(errorMessage, error.response?.status);
  }
};

export type FantasyWeekCalendarState = 'ACTIVE' | 'BREAK' | 'UNKNOWN';
export interface AdminFantasyWeekCalendarRow {
  fantasy_week_number: number;
  calendar_start_date: string;
  calendar_end_date: string;
  fixture_count: number;
  state: FantasyWeekCalendarState;
  reason?: string | null;
  label?: string | null;
  confirmed_at?: string | null;
  confirmed_by_user_id?: number | null;
  conflict?: string | null;
  affected_league_ids: number[];
  affected_league_weeks: Record<string, number>;
  playoff_conflict_league_ids: number[];
}
export interface AdminFantasyWeekCalendar {
  fantasy_season_definition_id: number;
  season_identifier: string;
  competition_id: number;
  competition_name: string;
  weeks: AdminFantasyWeekCalendarRow[];
}
export interface AdminFantasyWeekCalendarUpdateResult {
  status: string;
  state: 'BREAK' | 'CLEAR';
  fantasy_week_number: number;
  playoff_conflict_league_ids: number[];
}

export const fetchFantasyWeekCalendarAdmin = async (
  token: string,
  fsdId: number,
): Promise<AdminFantasyWeekCalendar> => {
  if (!token) throw new ApiError('Authentication is required.', 401);
  try {
    const response = await adminApiClient.get<AdminFantasyWeekCalendar>(
      `${API_BASE_URL}/admin/fantasy-season/${fsdId}/calendar-weeks`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  } catch (error: unknown) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const detail = isAxiosError(error) ? error.response?.data?.detail : null;
    throw new ApiError(
      typeof detail === 'string' ? detail : 'Failed to load the fantasy-week calendar.',
      status,
    );
  }
};

export const setFantasyWeekCalendarStateAdmin = async (
  token: string,
  fsdId: number,
  weekNumber: number,
  state: 'BREAK' | 'CLEAR',
  reason: 'INTERNATIONAL_BREAK' | 'WINTER_BREAK' | 'OTHER_NO_FIXTURES' = 'INTERNATIONAL_BREAK',
): Promise<AdminFantasyWeekCalendarUpdateResult> => {
  if (!token) throw new ApiError('Authentication is required.', 401);
  try {
    const response = await adminApiClient.put<AdminFantasyWeekCalendarUpdateResult>(
      `${API_BASE_URL}/admin/fantasy-season/${fsdId}/calendar-weeks/${weekNumber}`,
      {
        state,
        reason,
        label: {
          INTERNATIONAL_BREAK: 'International Break',
          WINTER_BREAK: 'Winter Break',
          OTHER_NO_FIXTURES: 'No League Matches',
        }[reason],
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  } catch (error: unknown) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const detail = isAxiosError(error) ? error.response?.data?.detail : null;
    throw new ApiError(
      typeof detail === 'string' ? detail : 'Failed to update the fantasy-week calendar.',
      status,
    );
  }
};
